/**
 * ENG-10075 — the manager's writer for `<projectDir>/.claude/settings.json`.
 *
 * ── Why this file exists at all ─────────────────────────────────────────────
 *
 * `advisor` is the first model slot with no environment variable. There is no
 * `ANTHROPIC_ADVISOR_MODEL`; Claude Code takes the value from `advisorModel` in
 * settings, or from `--advisor` on argv. Argv was rejected on ENG-9811 (Claude
 * Code EXITS at launch on an invalid pairing, which on a tmux-spawned agent is
 * spawn death against a breaker that trips at three), and user-level settings
 * were rejected because every agent on these hosts shares `HOME=/root`, making a
 * user-level value host-grain — `hosts.claude_auth_mode`'s defect rebuilt inside
 * the epic that exists to remove it.
 *
 * That leaves the per-agent PROJECT settings file, which ENG-9811's AC1 measured
 * as honoured AND as beating user scope (CLI 2.1.261, cells c and g —
 * docs/research/eng-9811-advisor-model-delivery.md).
 *
 * ── The trap this module was written to avoid ───────────────────────────────
 *
 * The claudecode adapter already emits a `settings.json`, and **Claude Code does
 * not read it.** Artefacts are materialised at `join(agentDir, relativePath)`
 * (manager-worker.ts), so it lands at `<agentDir>/settings.json`, while the
 * runtime reads `~/.claude/settings.json` and `<projectDir>/.claude/settings.json`.
 * ENG-5631 recorded this in claudecode/index.ts about its own `model` field,
 * which has been inert there ever since.
 *
 * Writing `advisorModel` into the adapter's file would therefore have produced
 * the exact failure ENG-9811 §5 exists to prevent: policy row set, file written,
 * drift tracker satisfied, every control-plane signal green, and no advisor —
 * indistinguishable from one that worked. Hence a separate writer, aimed at the
 * path the runtime actually reads.
 *
 * ── Ownership, and why the merge is not a clobber ───────────────────────────
 *
 * `<projectDir>/.claude/` is not ours alone — the spawner already puts
 * `persistent-claude.sh` and `agt-bin/` there, and an operator may have written
 * settings of their own. So this owns KEYS, not the file: it merges
 * `MANAGED_PROJECT_SETTINGS_KEYS` in, removes the managed keys the policy no
 * longer names, and leaves everything else untouched.
 *
 * The removal half is the one worth stating. A policy that stops naming an
 * advisor must actually stop delivering one; a writer that only ever added keys
 * would leave the last advisor an agent was given pinned for the life of the
 * host, surviving the policy edit that was meant to remove it — and doing so
 * silently, which is this epic's recurring failure shape.
 */
import { existsSync, mkdirSync, readFileSync, renameSync, unlinkSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';

import { MANAGED_PROJECT_SETTINGS_KEYS } from './model-policy.js';

/** What the writer did, so the caller can log it and a test can assert it. */
export type ProjectClaudeSettingsOutcome =
  /** Nothing to deliver and no file to clean up — the overwhelmingly common case. */
  | { readonly kind: 'noop' }
  /** The file already said exactly this. No write, so no mtime churn on every spawn. */
  | { readonly kind: 'unchanged'; readonly path: string }
  | { readonly kind: 'written'; readonly path: string; readonly keys: readonly string[] }
  /** Every managed key removed and nothing else left, so the file itself went. */
  | { readonly kind: 'removed'; readonly path: string }
  /**
   * Unparseable JSON, moved aside. `backup` is where the old bytes went.
   *
   * `rewritten` is false in the one case that would otherwise make the log line
   * a lie: the file was corrupt AND the policy sets nothing, so it was moved
   * away and nothing replaced it. Saying "rewrote it" there would send an
   * operator looking for a file that does not exist.
   */
  | {
      readonly kind: 'recovered';
      readonly path: string;
      readonly backup: string;
      readonly rewritten: boolean;
    }
  /** Fail-soft. The spawn continues; the advisor simply does not attach this time. */
  | { readonly kind: 'failed'; readonly path: string; readonly error: string };

/**
 * Merge this manager's owned keys into the project settings Claude Code reads.
 *
 * NEVER THROWS. A settings file we cannot write must not take an agent down —
 * the agent runs fine without an advisor, and a spawn crash over a model
 * preference would be far worse than the preference not arriving. The caller
 * logs the outcome; `failed` is visible rather than silent, which is the whole
 * point of returning it.
 */
export function writeProjectClaudeSettings(args: {
  readonly projectDir: string;
  /** The fragment from `resolveModelPolicySpawnEnv().projectSettings`. */
  readonly managed: Record<string, string>;
}): ProjectClaudeSettingsOutcome {
  const { projectDir, managed } = args;
  const path = join(projectDir, '.claude', 'settings.json');

  try {
    const fileExists = existsSync(path);

    // The cheap exit, taken by every agent on the fleet today: no managed keys
    // to write and no file to clean up. Deliberately BEFORE any read or mkdir,
    // so an agent with no advisor row touches the filesystem exactly as much as
    // it did before this module existed — which is what makes "byte-identical
    // to today" a fact rather than an intention.
    if (Object.keys(managed).length === 0 && !fileExists) return { kind: 'noop' };

    let existing: Record<string, unknown> = {};
    let backup: string | null = null;

    if (fileExists) {
      const raw = readFileSync(path, 'utf8');
      try {
        const parsed: unknown = JSON.parse(raw);
        // A JSON array or scalar parses fine and is not a settings object.
        // Treating it as one would spread its indices into our merge.
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
          existing = parsed as Record<string, unknown>;
        } else {
          throw new Error('settings.json is not a JSON object');
        }
      } catch {
        // Do NOT silently overwrite an operator's file, and do NOT bail either:
        // bailing leaves Claude Code reading a file it cannot parse, which is a
        // worse state than the one we found. Move the bytes aside so they are
        // recoverable, then write a clean file.
        backup = `${path}.corrupt-${Date.now()}`;
        renameSync(path, backup);
        existing = {};
      }
    }

    const next: Record<string, unknown> = { ...existing };
    for (const key of MANAGED_PROJECT_SETTINGS_KEYS) {
      if (key in managed) next[key] = managed[key];
      else delete next[key];
    }

    // Nothing of ours and nothing of anyone else's. Remove the file rather than
    // leaving `{}` behind: an empty settings.json is indistinguishable from a
    // deliberate one, and the next reader would have to guess whether some
    // policy had emptied it.
    if (Object.keys(next).length === 0) {
      // The corrupt file was already renamed away above, so there is nothing
      // left at `path` and nothing to put there. Reported as `recovered` with
      // `rewritten: false` rather than as `removed`, because the operator needs
      // the backup path — their bytes are still recoverable and a `removed`
      // notice would not say where.
      if (backup) return { kind: 'recovered', path, backup, rewritten: false };
      if (fileExists) {
        unlinkSync(path);
        return { kind: 'removed', path };
      }
      return { kind: 'noop' };
    }

    const serialized = `${JSON.stringify(next, null, 2)}\n`;
    if (!backup && fileExists && readFileSync(path, 'utf8') === serialized) {
      return { kind: 'unchanged', path };
    }

    mkdirSync(join(projectDir, '.claude'), { recursive: true });
    writeFileSync(path, serialized, { mode: 0o600 });
    if (backup) return { kind: 'recovered', path, backup, rewritten: true };
    return { kind: 'written', path, keys: Object.keys(managed) };
  } catch (err) {
    return { kind: 'failed', path, error: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * The manager.log line for an outcome, or null when there is nothing to say.
 *
 * Null for `noop` and `unchanged`, which together are every spawn of a settled
 * fleet — a line there is a line nobody reads, and this file follows
 * `resolveModelPolicySpawnEnv`'s existing rule about that. Everything else
 * CHANGED something on disk or failed to, and both are worth a line.
 */
export function projectClaudeSettingsNotice(
  codeName: string,
  outcome: ProjectClaudeSettingsOutcome,
): string | null {
  switch (outcome.kind) {
    case 'noop':
    case 'unchanged':
      return null;
    case 'written':
      return `[model-policy] '${codeName}' wrote ${outcome.keys.join(', ')} to ${outcome.path}`;
    case 'removed':
      return `[model-policy] '${codeName}' policy no longer sets any project setting — removed ${outcome.path}`;
    case 'recovered':
      return outcome.rewritten
        ? `[model-policy] '${codeName}' ${outcome.path} was not parseable JSON; moved to ${outcome.backup} and rewrote it`
        : `[model-policy] '${codeName}' ${outcome.path} was not parseable JSON; moved to ${outcome.backup} and did NOT replace it (the policy sets nothing)`;
    case 'failed':
      return `[model-policy] '${codeName}' could NOT write ${outcome.path} (${outcome.error}) — agent spawns without it`;
  }
}
